/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { APIContext } from "../../api";
import profilePhoto from "../../assets/profile_photo.svg";
import HelmetLayout from "../../components/HelmetLayout";
import {
    Button,
    Textarea,
    useToast,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Tag,
    Divider,
    Grid,
    GridItem,
    Box,
    Image,
    Heading,
    Text,
    Badge,
    Link,
    List,
    ListItem,
    Checkbox,
    FormControl,
    FormLabel,
    Input,
} from "@chakra-ui/react";
import { ChevronRightIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { PulseLoader } from "react-spinners";
import DateDisplay from "../../components/ui-kit/date-display";

const PENDING_DETAIL_KEYS = [
    { key: "app_name", label: "App name" },
    { key: "icon_url", label: "App icon" },
    { key: "developer_name", label: "Developer name" },
    { key: "status", label: "Status" },
    { key: "redirect_uris", label: "Redirect URIs", isArray: true },
    { key: "scopes", label: "Scopes", isArray: true },
    { key: "description", label: "Tagline / short description" },
    { key: "long_description", label: "Long description" },
    { key: "store_listing_category", label: "Category" },
    { key: "store_listing_pricing", label: "Pricing" },
    { key: "store_listing_features", label: "Key features", isArray: true },
    { key: "contact_email", label: "Contact email" },
    { key: "documentation_url", label: "Documentation URL" },
    { key: "support_url", label: "Support URL" },
    { key: "privacy_policy_url", label: "Privacy policy URL" },
    { key: "terms_of_service_url", label: "Terms of service URL" },
    { key: "homepage_url", label: "Homepage URL" },
];

function formatPendingValue(value, isArray) {
    if (value == null || value === "") return "—";
    if (isArray && Array.isArray(value)) return value.length ? value.join(", ") : "—";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

/** True if both URLs are this app's icon (canonical or versioned). Same icon with different URL should not be shown as a change. */
function isSameAppIconUrl(appId, url1, url2) {
    if (!appId) return false;
    const prefix = `oauth-apps/${appId}/icon`;
    const toPath = (u) => {
        if (!u || typeof u !== "string") return "";
        try {
            if (u.startsWith("http")) return new URL(u).pathname.replace(/^\//, "");
        } catch (_) {}
        return String(u).replace(/^\//, "");
    };
    const p1 = toPath(url1);
    const p2 = toPath(url2);
    const isAppIcon = (p) =>
        p === `${prefix}.avif` || p === `${prefix}.svg` || (p.startsWith(`${prefix}-`) && (p.endsWith(".avif") || p.endsWith(".svg")));
    return isAppIcon(p1) && isAppIcon(p2);
}

/** Normalize store_listing_media to array of { url, type? } (handles string URLs, objects, or JSON string from API). */
function normalizeStoreMedia(arr) {
    if (arr == null) return [];
    if (typeof arr === "string") {
        try {
            arr = JSON.parse(arr);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(arr)) return [];
    return arr.map((item) =>
        typeof item === "string" ? { url: item } : { ...item, url: item.url || item.src }
    ).filter((m) => m.url);
}

/** Single media item: image or video thumbnail. */
function StoreMediaItem({ media, size = "120px" }) {
    const url = media?.url || media?.src;
    if (!url) return null;
    const isVideo = (media?.type && String(media.type).toLowerCase() === "video") ||
        (!media?.type && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url));
    return (
        <Box borderRadius="md" w={size} h={size} flexShrink={0} overflow="hidden" bg="black">
            {isVideo ? (
                <video src={url} style={{ width: "100%", height: "100%", objectFit: "contain" }} preload="metadata" muted />
            ) : (
                <Image src={url} w="100%" h="100%" objectFit="cover" fallbackSrc="https://via.placeholder.com/120?text=Media" />
            )}
        </Box>
    );
}

function AppDetails() {
    const { appID } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { getAppDetails, reviewApp, unapproveApp, updateAppFeatured, deleteApp } = useContext(APIContext);

    const [app, setApp] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [reviewerNotes, setReviewerNotes] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUnapproving, setIsUnapproving] = useState(false);
    const [featuredApp, setFeaturedApp] = useState(false);
    const [featuredIndex, setFeaturedIndex] = useState("");
    const [isSavingFeatured, setIsSavingFeatured] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchDetails = () => {
        setIsLoading(true);
        getAppDetails(appID)
            .then((data) => {
                setApp(data);
                setReviewerNotes(data.reviewer_notes || "");
                setFeaturedApp(!!data.featured_app);
                setFeaturedIndex(data.featured_index != null ? String(data.featured_index) : "");
            })
            .catch((error) => {
                console.error("Error fetching app details:", error);
                toast({
                    title: "Error",
                    description: "Could not fetch app details",
                    status: "error",
                    duration: 3000,
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchDetails();
    }, [appID]);

    const handleReview = (status) => {
        if ((status === "rejected" || status === "reject_update") && !(reviewerNotes || "").trim()) {
            toast({
                title: "Message required",
                description: status === "reject_update" ? "Please enter feedback for the developer before rejecting the update." : "Please enter feedback for the developer before rejecting.",
                status: "warning",
                duration: 4000,
            });
            return;
        }
        setIsSubmitting(true);
        reviewApp(appID, status, reviewerNotes)
            .then(() => {
                toast({
                    title: "Success",
                    description: `App ${status} successfully`,
                    status: "success",
                    duration: 3000,
                });
                fetchDetails();
            })
            .catch((error) => {
                console.error(`Error ${status}ing app:`, error);
                toast({
                    title: "Error",
                    description: `Failed to ${status} app`,
                    status: "error",
                    duration: 3000,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleUnapprove = () => {
        if (!window.confirm("Remove this app from the Grigora app store? It will move to development and the developer will be notified by email.")) return;
        setIsUnapproving(true);
        unapproveApp(appID)
            .then(() => {
                toast({
                    title: "App unapproved",
                    description: "App has been removed from the app store and the developer has been notified.",
                    status: "success",
                    duration: 4000,
                });
                fetchDetails();
            })
            .catch((error) => {
                console.error("Error unapproving app:", error);
                toast({
                    title: "Error",
                    description: error?.response?.data?.error || "Failed to unapprove app",
                    status: "error",
                    duration: 3000,
                });
            })
            .finally(() => setIsUnapproving(false));
    };

    const handleDeleteApp = () => {
        if (!window.confirm(`Permanently delete "${app?.app_name || "this app"}"? This cannot be undone. All app data will be removed.`)) return;
        setIsDeleting(true);
        deleteApp(appID)
            .then(() => {
                toast({
                    title: "App deleted",
                    description: "The app has been permanently deleted.",
                    status: "success",
                    duration: 4000,
                });
                navigate("/apps");
            })
            .catch((error) => {
                console.error("Error deleting app:", error);
                toast({
                    title: "Error",
                    description: error?.response?.data?.error || "Failed to delete app",
                    status: "error",
                    duration: 4000,
                });
            })
            .finally(() => setIsDeleting(false));
    };

    const handleSaveFeatured = () => {
        const payload = { featured_app: featuredApp };
        if (featuredApp) {
            const n = parseInt(featuredIndex, 10);
            payload.featured_index = Number.isNaN(n) || n < 0 ? 0 : n;
        } else {
            payload.featured_index = null;
        }
        setIsSavingFeatured(true);
        updateAppFeatured(appID, payload)
            .then(() => {
                toast({ title: "Featured settings saved", status: "success", duration: 3000 });
                fetchDetails();
            })
            .catch((err) => {
                toast({
                    title: "Error",
                    description: err?.response?.data?.error || "Failed to update featured settings",
                    status: "error",
                    duration: 3000,
                });
            })
            .finally(() => setIsSavingFeatured(false));
    };

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <PulseLoader size={10} color="#8593ED" />
            </div>
        );
    }

    if (!app) {
        return <div className="p-7">App not found</div>;
    }

    const snapshot = app.pending_revision?.revision_snapshot;
    const displayIconUrl = snapshot?.icon_url ?? app.icon_url;
    const displayStoreMedia = (Array.isArray(snapshot?.store_listing_media) && snapshot.store_listing_media.length > 0)
        ? snapshot.store_listing_media
        : (app.store_listing_media || []);

    return (
        <>
            <HelmetLayout title={`App: ${app.app_name}`} />
            <div className="space-y-6 pb-12">
                <div className="flex flex-col justify-start items-start max-w-5xl">
                    <div className="w-full flex flex-row justify-between mb-4">
                        <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />}>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/apps" target="_blank" rel="noopener noreferrer">Apps</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem isCurrentPage>
                                <BreadcrumbLink>{app.app_name}</BreadcrumbLink>
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </div>

                    <div className="w-full flex justify-between items-end mb-8">
                        <div className="flex items-center gap-4">
                            {displayIconUrl ? (
                                <Image src={displayIconUrl} boxSize="80px" borderRadius="xl" objectFit="cover" />
                            ) : (
                                <Box boxSize="80px" bg="gray.100" borderRadius="xl" display="flex" alignItems="center" justifyCenter />
                            )}
                            <div>
                                <Heading size="lg" mb={1}>{app.app_name}</Heading>
                                <Text color="gray.500">by {app.developer_name || "Unknown Developer"}</Text>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Badge
                                colorScheme={
                                    app.review_status === "pending" ? "yellow" :
                                    app.review_status === "approved" || app.status === "production" ? "green" :
                                    app.review_status === "rejected" ? "red" : "gray"
                                }
                                p={2}
                                borderRadius="md"
                                px={4}
                            >
                                {app.review_status === "pending"
                                    ? "PENDING"
                                    : app.review_status === "approved" || app.status === "production"
                                        ? "APPROVED"
                                        : app.review_status === "rejected"
                                            ? "REJECTED"
                                            : "DEVELOPMENT"}
                            </Badge>
                        </div>
                    </div>

                    <Grid templateColumns="repeat(3, 1fr)" gap={6} w="full">
                        <GridItem colSpan={2}>
                            {app.pending_revision && (
                                <Box bg="blue.50" p={6} borderRadius="2xl" mb={6} borderWidth="1px" borderColor="blue.200">
                                    <Heading size="sm" mb={4} color="blue.700" textTransform="uppercase">Pending changes (this version)</Heading>
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                                        <Box>
                                            <Text fontSize="xs" color="gray.600" fontWeight="bold">Proposed version</Text>
                                            <Text fontSize="sm" fontWeight="medium">{app.pending_revision.proposed_version || "—"}</Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.600" fontWeight="bold">Change type</Text>
                                            <Text fontSize="sm" className="capitalize">{app.pending_revision.change_type || "—"}</Text>
                                        </Box>
                                    </Grid>
                                    {app.pending_revision.changelog_description && (
                                        <Box mb={4}>
                                            <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>Changelog description</Text>
                                            <Text fontSize="sm" whiteSpace="pre-wrap">{app.pending_revision.changelog_description}</Text>
                                        </Box>
                                    )}
                                    {(() => {
                                        const snapshot = app.pending_revision?.revision_snapshot;
                                        const iconChanged = snapshot && app.id && !isSameAppIconUrl(app.id, app.icon_url, snapshot.icon_url) && JSON.stringify(app.icon_url) !== JSON.stringify(snapshot.icon_url);
                                        let displaySummary = Array.isArray(app.pending_changes_summary) ? [...app.pending_changes_summary] : [];
                                        displaySummary = displaySummary.map((item) => (item === "icon url" ? "App icon" : item));
                                        if (iconChanged && !displaySummary.some((s) => s === "App icon")) {
                                            displaySummary = ["App icon", ...displaySummary];
                                        }
                                        if (displaySummary.length === 0) return null;
                                        return (
                                            <>
                                                <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={2}>What changed</Text>
                                                <List spacing={1}>
                                                    {displaySummary.map((item, idx) => (
                                                        <ListItem key={idx} fontSize="sm" display="flex" alignItems="flex-start">
                                                            <Text as="span" mr={2}>•</Text>
                                                            <Text as="span">{item}</Text>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        );
                                    })()}
                                    {app.pending_revision?.revision_snapshot && (
                                        <Box mt={4} pt={4} borderTopWidth="1px" borderColor="blue.200">
                                            <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={3}>Current vs proposed (detail)</Text>
                                            <Box as="table" w="full" fontSize="sm" sx={{ "& td": { py: 1.5, verticalAlign: "top" } }}>
                                                <tbody>
                                                    {PENDING_DETAIL_KEYS.map(({ key, label, isArray }) => {
                                                        const cur = app[key];
                                                        const next = app.pending_revision.revision_snapshot[key];
                                                        if (key === "icon_url" && isSameAppIconUrl(app.id, cur, next)) return null;
                                                        const curStr = JSON.stringify(cur);
                                                        const nextStr = JSON.stringify(next);
                                                        if (curStr === nextStr) return null;
                                                        // App icon: show actual images instead of links
                                                        if (key === "icon_url") {
                                                            return (
                                                                <tr key={key}>
                                                                    <td width="140px" fontWeight="600" color="gray.600">{label}</td>
                                                                    <td width="45%" pr={2}>
                                                                        <Text fontSize="xs" color="gray.500" mb={0.5}>Current</Text>
                                                                        <Box mt={1}>
                                                                            {cur ? (
                                                                                <Image src={cur} boxSize="64px" borderRadius="lg" objectFit="cover" fallbackSrc="https://via.placeholder.com/64?text=Icon" />
                                                                            ) : (
                                                                                <Box boxSize="64px" bg="gray.200" borderRadius="lg" />
                                                                            )}
                                                                        </Box>
                                                                    </td>
                                                                    <td width="45%">
                                                                        <Text fontSize="xs" color="blue.600" fontWeight="600" mb={0.5}>Proposed</Text>
                                                                        <Box mt={1}>
                                                                            {next ? (
                                                                                <Image src={next} boxSize="64px" borderRadius="lg" objectFit="cover" fallbackSrc="https://via.placeholder.com/64?text=Icon" />
                                                                            ) : (
                                                                                <Box boxSize="64px" bg="gray.200" borderRadius="lg" />
                                                                            )}
                                                                        </Box>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                        return (
                                                            <tr key={key}>
                                                                <td width="140px" fontWeight="600" color="gray.600">{label}</td>
                                                                <td width="45%" pr={2}>
                                                                    <Text fontSize="xs" color="gray.500" mb={0.5}>Current</Text>
                                                                    <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-all">
                                                                        {formatPendingValue(cur, isArray)}
                                                                    </Text>
                                                                </td>
                                                                <td width="45%">
                                                                    <Text fontSize="xs" color="blue.600" fontWeight="600" mb={0.5}>Proposed</Text>
                                                                    <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-all" color="blue.700">
                                                                        {formatPendingValue(next, isArray)}
                                                                    </Text>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {app.pending_revision.revision_snapshot.dashboard_page_link !== undefined && (
                                                        (() => {
                                                            const cur = app.dashboard_page_link;
                                                            const next = app.pending_revision.revision_snapshot.dashboard_page_link;
                                                            const curStr = cur ? `${cur.type}: ${cur.url}` : "—";
                                                            const nextStr = next && next.type ? `${next.type}: ${next.url || "—"}` : "—";
                                                            if (curStr === nextStr) return null;
                                                            return (
                                                                <tr key="dashboard_page_link">
                                                                    <td width="140px" fontWeight="600" color="gray.600">Dashboard page</td>
                                                                    <td width="45%" pr={2}>
                                                                        <Text fontSize="xs" color="gray.500" mb={0.5}>Current</Text>
                                                                        <Text fontSize="sm">{curStr}</Text>
                                                                    </td>
                                                                    <td width="45%">
                                                                        <Text fontSize="xs" color="blue.600" fontWeight="600" mb={0.5}>Proposed</Text>
                                                                        <Text fontSize="sm" color="blue.700">{nextStr}</Text>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })()
                                                    )}
                                                </tbody>
                                            </Box>

                                            {/* Store listing media: current vs proposed with actual images/videos */}
                                            {(() => {
                                                const curMedia = normalizeStoreMedia(app.store_listing_media);
                                                const nextMedia = normalizeStoreMedia(app.pending_revision.revision_snapshot.store_listing_media);
                                                const curUrls = curMedia.map((m) => (m.url || "").split("?")[0]);
                                                const nextUrls = nextMedia.map((m) => (m.url || "").split("?")[0]);
                                                const same = curUrls.length === nextUrls.length && curUrls.every((u, i) => u === nextUrls[i]);
                                                if (same) return null;
                                                return (
                                                    <Box mt={6} pt={4} borderTopWidth="1px" borderColor="blue.200">
                                                        <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={3}>Store listing media</Text>
                                                        <Grid templateColumns="1fr 1fr" gap={4}>
                                                            <Box>
                                                                <Text fontSize="xs" color="gray.500" fontWeight="600" mb={2}>Current ({curMedia.length})</Text>
                                                                <Box display="flex" flexWrap="wrap" gap={2}>
                                                                    {curMedia.length ? curMedia.map((media, idx) => (
                                                                        <StoreMediaItem key={idx} media={media} size="100px" />
                                                                    )) : (
                                                                        <Text fontSize="sm" color="gray.500">— None</Text>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color="blue.600" fontWeight="600" mb={2}>Proposed ({nextMedia.length})</Text>
                                                                <Box display="flex" flexWrap="wrap" gap={2}>
                                                                    {nextMedia.length ? nextMedia.map((media, idx) => (
                                                                        <StoreMediaItem key={idx} media={media} size="100px" />
                                                                    )) : (
                                                                        <Text fontSize="sm" color="gray.500">— None</Text>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                        {(curUrls.some((u) => !nextUrls.includes(u)) || nextUrls.some((u) => !curUrls.includes(u))) && (
                                                            <Box mt={2} fontSize="xs" color="gray.600">
                                                                {curUrls.some((u) => !nextUrls.includes(u)) && (
                                                                    <Text mb={0.5}>Removed: {curMedia.filter((m) => !nextUrls.includes((m.url || "").split("?")[0])).length} item(s)</Text>
                                                                )}
                                                                {nextUrls.some((u) => !curUrls.includes(u)) && (
                                                                    <Text>Added: {nextMedia.filter((m) => !curUrls.includes((m.url || "").split("?")[0])).length} item(s)</Text>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })()}

                                            {/* Embedded scripts: full diff with code (added / removed / edited) */}
                                            {app.pending_revision.revision_snapshot.embedded_scripts != null && (() => {
                                                const curScripts = Array.isArray(app.embedded_scripts) ? app.embedded_scripts : [];
                                                const nextScripts = Array.isArray(app.pending_revision.revision_snapshot.embedded_scripts)
                                                    ? app.pending_revision.revision_snapshot.embedded_scripts : [];
                                                const curByName = Object.fromEntries((curScripts || []).map((s, i) => [(s.name && String(s.name).trim()) || `__idx_${i}`, s]));
                                                const nextByName = Object.fromEntries((nextScripts || []).map((s, i) => [(s.name && String(s.name).trim()) || `__idx_${i}`, s]));
                                                const allNames = [...new Set([...Object.keys(curByName), ...Object.keys(nextByName)])];
                                                const hasDiff = allNames.some((name) => {
                                                    const c = curByName[name];
                                                    const n = nextByName[name];
                                                    if (!c && n) return true;
                                                    if (c && !n) return true;
                                                    if (c && n) return (c.code || "") !== (n.code || "") || (c.script_type || "") !== (n.script_type || "") || (c.script_placement || "") !== (n.script_placement || "");
                                                    return false;
                                                });
                                                if (!hasDiff) return null;
                                                return (
                                                    <Box mt={6} pt={4} borderTopWidth="1px" borderColor="blue.200">
                                                        <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={3}>Embedded scripts (current vs proposed)</Text>
                                                        <List listStyleType="none" p={0} m={0}>
                                                            {allNames.map((name) => {
                                                                const cur = curByName[name];
                                                                const next = nextByName[name];
                                                                const added = !cur && next;
                                                                const removed = cur && !next;
                                                                const edited = cur && next && (cur.code !== next.code || cur.script_type !== next.script_type || cur.script_placement !== next.script_placement);
                                                                if (!added && !removed && !edited) return null;
                                                                const displayName = name.startsWith("__idx_") ? `Script ${Number(name.replace("__idx_", "")) + 1}` : name;
                                                                const label = added ? `Added: ${displayName}` : removed ? `Removed: ${displayName}` : `Edited: ${displayName}`;
                                                                const badge = added ? "green" : removed ? "red" : "orange";
                                                                return (
                                                                    <ListItem key={name} mb={4} p={3} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                                                        <Badge colorScheme={badge} mb={2}>{label}</Badge>
                                                                        <Grid templateColumns={edited ? "1fr 1fr" : "1fr"} gap={3}>
                                                                            {(cur || removed) && (
                                                                                <Box>
                                                                                    <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1}>
                                                                                        {removed ? "Previous code (removed)" : "Current"}
                                                                                    </Text>
                                                                                    <Text fontSize="xs" color="gray.600">
                                                                                        Type: {cur?.script_type || "—"} | Placement: {cur?.script_placement || "—"}
                                                                                    </Text>
                                                                                    <Box mt={1} p={2} bg="gray.900" color="green.300" borderRadius="md" fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-all" maxH="200px" overflowY="auto">
                                                                                        {cur?.code || "(empty)"}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                            {(next || added) && (
                                                                                <Box>
                                                                                    <Text fontSize="xs" color="blue.600" fontWeight="600" mb={1}>
                                                                                        {added ? "New code (added)" : "Proposed"}
                                                                                    </Text>
                                                                                    <Text fontSize="xs" color="gray.600">
                                                                                        Type: {next?.script_type || "—"} | Placement: {next?.script_placement || "—"}
                                                                                    </Text>
                                                                                    <Box mt={1} p={2} bg="gray.900" color="green.300" borderRadius="md" fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-all" maxH="200px" overflowY="auto">
                                                                                        {next?.code ?? "(empty)"}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Grid>
                                                                    </ListItem>
                                                                );
                                                            })}
                                                        </List>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                            )}
                            <Box bg="gray.50" p={6} borderRadius="2xl" mb={6}>
                                <Heading size="sm" mb={4} color="gray.600" textTransform="uppercase">Description</Heading>
                                <Text mb={4} fontWeight="medium">{app.description || "—"}</Text>
                                <Divider mb={4} />
                                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{app.long_description || "No long description provided."}</Text>
                            </Box>

                            <Box bg="gray.50" p={6} borderRadius="2xl" mb={6}>
                                <Heading size="sm" mb={4} color="gray.600" textTransform="uppercase">Product / Store listing</Heading>
                                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Category</Text>
                                        <Text fontSize="sm">{app.store_listing_category || "—"}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold">Pricing</Text>
                                        <Text fontSize="sm">{app.store_listing_pricing || "—"}</Text>
                                    </Box>
                                    {app.version && (
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold">Version</Text>
                                            <Text fontSize="sm">{app.version}</Text>
                                        </Box>
                                    )}
                                </Grid>
                                {(app.store_listing_features && app.store_listing_features.length > 0) && (
                                    <>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2}>Key features</Text>
                                        <List spacing={1}>
                                            {app.store_listing_features.filter(Boolean).map((feature, idx) => (
                                                <ListItem key={idx} fontSize="sm" display="flex" alignItems="flex-start">
                                                    <Text as="span" mr={2}>•</Text>
                                                    <Text as="span">{feature}</Text>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </Box>

                            <Box bg="gray.50" p={6} borderRadius="2xl" mb={6}>
                                <Heading size="sm" mb={4} color="gray.600" textTransform="uppercase">Resource links</Heading>
                                <List spacing={3}>
                                    <ListItem>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={0.5}>Documentation</Text>
                                        {app.documentation_url ? (
                                            <Link target="_blank" rel="noopener noreferrer" href={app.documentation_url} isExternal fontSize="sm" color="blue.600">
                                                {app.documentation_url} <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        ) : (
                                            <Text fontSize="sm">—</Text>
                                        )}
                                    </ListItem>
                                    <ListItem>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={0.5}>Support</Text>
                                        {app.support_url ? (
                                            <Link target="_blank" rel="noopener noreferrer" href={app.support_url} isExternal fontSize="sm" color="blue.600">
                                                {app.support_url} <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        ) : (
                                            <Text fontSize="sm">—</Text>
                                        )}
                                    </ListItem>
                                    <ListItem>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={0.5}>Privacy policy</Text>
                                        {app.privacy_policy_url ? (
                                            <Link target="_blank" rel="noopener noreferrer" href={app.privacy_policy_url} isExternal fontSize="sm" color="blue.600">
                                                {app.privacy_policy_url} <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        ) : (
                                            <Text fontSize="sm">—</Text>
                                        )}
                                    </ListItem>
                                    <ListItem>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={0.5}>Terms of service</Text>
                                        {app.terms_of_service_url ? (
                                            <Link target="_blank" rel="noopener noreferrer" href={app.terms_of_service_url} isExternal fontSize="sm" color="blue.600">
                                                {app.terms_of_service_url} <ExternalLinkIcon mx="2px" />
                                            </Link>
                                        ) : (
                                            <Text fontSize="sm">—</Text>
                                        )}
                                    </ListItem>
                                </List>
                                {(app.homepage_url || app.contact_email) && (
                                    <>
                                        <Divider my={4} />
                                        {app.homepage_url && (
                                            <Box mb={2}>
                                                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={0.5}>Homepage</Text>
                                                <Link target="_blank" rel="noopener noreferrer" href={app.homepage_url} isExternal fontSize="sm" color="blue.600">
                                                    {app.homepage_url} <ExternalLinkIcon mx="2px" />
                                                </Link>
                                            </Box>
                                        )}
                                        {app.contact_email && (
                                            <Box>
                                                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={0.5}>Contact email</Text>
                                                <Link target="_blank" rel="noopener noreferrer" href={`mailto:${app.contact_email}`} fontSize="sm" color="blue.600">
                                                    {app.contact_email}
                                                </Link>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>

                            <Box bg="gray.50" p={6} borderRadius="2xl">
                                <Heading size="sm" mb={4} color="gray.600">Technical Details</Heading>
                                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold">CLIENT ID</Text>
                                        <Text fontSize="sm" fontFamily="mono">{app.client_id}</Text>
                                    </Box>
                                    <Box colSpan={2}>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mt={2}>REDIRECT URIS</Text>
                                        <Box mt={1}>
                                            {app.redirect_uris?.map((uri, idx) => (
                                                <Tag key={idx} size="sm" mr={1} mb={1} colorScheme="blue" variant="outline">{uri}</Tag>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box colSpan={2}>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mt={2}>SCOPES</Text>
                                        <Box mt={1}>
                                            {app.scopes?.map((scope, idx) => (
                                                <Tag key={idx} size="sm" mr={1} mb={1} colorScheme="purple" variant="subtle">{scope}</Tag>
                                            ))}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Box>

                            {app.embedded_scripts && app.embedded_scripts.length > 0 && (
                                <Box bg="gray.50" p={6} borderRadius="2xl" mt={6}>
                                    <Heading size="sm" mb={4} color="gray.600">Embedded Scripts</Heading>
                                    <Grid templateColumns="repeat(1, 1fr)" gap={4}>
                                        {app.embedded_scripts.map((script, idx) => (
                                            <Box key={idx} p={3} bg="white" borderRadius="lg" border="1px" borderColor="gray.200">
                                                <div className="flex justify-between items-center mb-1">
                                                    <Text fontWeight="bold" fontSize="sm">{script.name}</Text>
                                                    <Badge colorScheme={script.disabled ? "red" : "green"}>{script.disabled ? "Disabled" : "Active"}</Badge>
                                                </div>
                                                <Text fontSize="xs" color="gray.500">Type: {script.script_type} | Placement: {script.script_placement}</Text>
                                                {script.code && (
                                                    <Box mt={2} p={2} bg="gray.900" color="green.300" borderRadius="md" fontSize="xs" fontFamily="mono" maxH="100px" overflowY="auto">
                                                        {script.code}
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {app.dashboard_page_link && (
                                <Box bg="gray.50" p={6} borderRadius="2xl" mt={6}>
                                    <Heading size="sm" mb={4} color="gray.600">Dashboard Page</Heading>
                                    <Box p={3} bg="white" borderRadius="lg" border="1px" borderColor="gray.200">
                                        <Text fontWeight="bold" fontSize="sm" mb={1} className="capitalize">{app.dashboard_page_link.type} Integration</Text>
                                        <Text fontSize="xs" fontFamily="mono" color="blue.500" as="a" href={app.dashboard_page_link.url} target="_blank" rel="noopener noreferrer">
                                            {app.dashboard_page_link.url}
                                        </Text>
                                    </Box>
                                </Box>
                            )}

                            {displayStoreMedia && displayStoreMedia.length > 0 && (
                                <Box mt={8}>
                                    <Heading size="sm" mb={4} color="gray.600">Store Media</Heading>
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                        {displayStoreMedia.map((media, idx) => {
                                            const isVideo = (media.type && String(media.type).toLowerCase() === "video") || (!media.type && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(media.url || ""));
                                            const url = media.url || media.src;
                                            if (!url) return null;
                                            return (
                                                <GridItem key={idx}>
                                                    {isVideo ? (
                                                        <Box borderRadius="lg" w="full" h="200px" bg="black" overflow="hidden">
                                                            <video
                                                                src={url}
                                                                controls
                                                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                preload="metadata"
                                                            >
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        </Box>
                                                    ) : (
                                                        <Image src={url} borderRadius="lg" w="full" h="200px" objectFit="cover" fallbackSrc="https://via.placeholder.com/150" />
                                                    )}
                                                </GridItem>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )}
                        </GridItem>

                        <GridItem colSpan={1}>
                            <Box bg="white" border="1px" borderColor="gray.100" p={6} borderRadius="2xl" position="sticky" top="20px">
                                <Heading size="sm" mb={4} color="gray.600">Review Application</Heading>

                                {(() => {
                                    const isPending = app.review_status === "pending";
                                    const isApproved = app.review_status === "approved" || app.status === "production";
                                    const isRejected = app.review_status === "rejected";

                                    if (isPending) {
                                        const isUpdateReview = app.status === "production" && app.pending_revision;
                                        return (
                                            <>
                                                <Badge colorScheme="yellow" p={2} borderRadius="md" px={4} mb={4} w="full" justifyContent="center">
                                                    {isUpdateReview ? "Pending update review" : "Pending review"}
                                                </Badge>
                                                <Text fontSize="sm" color="gray.600" mb={2}>Reviewer notes (visible to developer){isUpdateReview ? " — required when rejecting update:" : " — required when rejecting:"}</Text>
                                                <Textarea
                                                    placeholder={isUpdateReview ? "Enter feedback for the developer (required to reject update)..." : "Enter feedback for the developer (required to reject)..."}
                                                    value={reviewerNotes}
                                                    onChange={(e) => setReviewerNotes(e.target.value)}
                                                    mb={4}
                                                    size="sm"
                                                    borderRadius="xl"
                                                    rows={6}
                                                />
                                                <div className="flex flex-col gap-2">
                                                    <Button
                                                        colorScheme="green"
                                                        w="full"
                                                        borderRadius="xl"
                                                        onClick={() => handleReview("approved")}
                                                        isLoading={isSubmitting}
                                                    >
                                                        {isUpdateReview ? "Approve update" : "Approve App"}
                                                    </Button>
                                                    {isUpdateReview ? (
                                                        <Button
                                                            colorScheme="orange"
                                                            variant="outline"
                                                            w="full"
                                                            borderRadius="xl"
                                                            onClick={() => handleReview("reject_update")}
                                                            isLoading={isSubmitting}
                                                            isDisabled={!(reviewerNotes || "").trim()}
                                                        >
                                                            Reject update
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            colorScheme="red"
                                                            variant="outline"
                                                            w="full"
                                                            borderRadius="xl"
                                                            onClick={() => handleReview("rejected")}
                                                            isLoading={isSubmitting}
                                                            isDisabled={!(reviewerNotes || "").trim()}
                                                        >
                                                            Reject App
                                                        </Button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    }

                                    if (isApproved) {
                                        return (
                                            <>
                                                <Badge colorScheme="green" p={2} borderRadius="md" px={4} mb={4} w="full" justifyContent="center">
                                                    Approved
                                                </Badge>
                                                <Text fontSize="sm" color="gray.600" mb={4}>
                                                    This app has been approved and is live in the app store.
                                                </Text>
                                                <Button
                                                    colorScheme="orange"
                                                    variant="outline"
                                                    w="full"
                                                    borderRadius="xl"
                                                    onClick={handleUnapprove}
                                                    isLoading={isUnapproving}
                                                >
                                                    Unapprove (remove from app store)
                                                </Button>
                                                <Text fontSize="xs" color="gray.500" mt={2}>
                                                    The app will move to development and the developer will be notified by email.
                                                </Text>
                                            </>
                                        );
                                    }

                                    if (isRejected) {
                                        return (
                                            <>
                                                <Badge colorScheme="red" p={2} borderRadius="md" px={4} mb={2} w="full" justifyContent="center">
                                                    Rejected
                                                </Badge>
                                                <Text fontSize="sm" color="gray.600" mb={4}>
                                                    This app was rejected. Rejected apps cannot be approved. The developer may address feedback and resubmit.
                                                </Text>
                                                {app.reviewer_notes && (
                                                    <Box mt={2} p={3} bg="gray.50" borderRadius="md">
                                                        <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Reviewer notes sent to developer</Text>
                                                        <Text fontSize="sm" whiteSpace="pre-wrap">{app.reviewer_notes}</Text>
                                                    </Box>
                                                )}
                                            </>
                                        );
                                    }

                                    return (
                                        <>
                                            <Text fontSize="sm" color="gray.600" mb={4}>
                                                This app is in development and has not been submitted for review.
                                            </Text>
                                            <Button
                                                colorScheme="red"
                                                variant="outline"
                                                w="full"
                                                borderRadius="xl"
                                                onClick={handleDeleteApp}
                                                isLoading={isDeleting}
                                            >
                                                Permanently delete app
                                            </Button>
                                            <Text fontSize="xs" color="gray.500" mt={2}>
                                                This will remove the app and all its data. This action cannot be undone.
                                            </Text>
                                        </>
                                    );
                                })()}

                                {(app.review_status === "approved" || app.status === "production") && (
                                    <>
                                        <Divider my={6} />
                                        <Heading size="sm" mb={4} color="gray.600">Featured in store</Heading>
                                        <Text fontSize="sm" color="gray.500" mb={3}>
                                            Feature this app at the start of the app store list. Lower index = higher position (0 = first).
                                        </Text>
                                        <FormControl mb={3}>
                                            <Checkbox
                                                isChecked={featuredApp}
                                                onChange={(e) => setFeaturedApp(e.target.checked)}
                                            >
                                                Featured app
                                            </Checkbox>
                                        </FormControl>
                                        {featuredApp && (
                                            <FormControl mb={4}>
                                                <FormLabel fontSize="xs" color="gray.600">Featured index (0 = first)</FormLabel>
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    value={featuredIndex}
                                                    onChange={(e) => setFeaturedIndex(e.target.value)}
                                                    size="sm"
                                                    borderRadius="lg"
                                                />
                                            </FormControl>
                                        )}
                                        <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={handleSaveFeatured}
                                            isLoading={isSavingFeatured}
                                        >
                                            Save featured settings
                                        </Button>
                                    </>
                                )}

                                <Divider my={6} />

                                <Box mb={4}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="bold">SUBMITTED ON</Text>
                                    <Text fontSize="sm">
                                        {app.created_at ? <DateDisplay date={app.created_at} /> : "-"}
                                    </Text>
                                </Box>

                                <Box mb={4}>
                                    <Text fontSize="xs" color="gray.500" fontWeight="bold">LAST UPDATED</Text>
                                    <Text fontSize="sm">
                                        {app.updated_at ? <DateDisplay date={app.updated_at} /> : "-"}
                                    </Text>
                                </Box>

                                {app.resolved_at && (
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="bold">RESOLVED AT</Text>
                                        <Text fontSize="sm">
                                            <DateDisplay date={app.resolved_at} />
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                        </GridItem>
                    </Grid>
                </div>
            </div>
        </>
    );
}

export default AppDetails;
